if (!customElements.get('combined-listings')) {
  customElements.define('combined-listings', class extends HTMLElement {
    connectedCallback() {
      customElements.whenDefined('variant-selects').then(() => {
        this.waitForVariations();
      });
    }

    waitForVariations() {
      const vs = document.getElementById('variant-selects-' + this.dataset.section);
      if (vs && vs.querySelector('.variations')) {
        this.init();
      } else {
        requestAnimationFrame(() => this.waitForVariations());
      }
    }

    init() {
      this.sectionId = this.dataset.section;
      // Translated in locales/*.json as products.product.family_type.
      this.typeLabel = this.dataset.typeLabel || 'Type';
      this.variantSelects = document.getElementById('variant-selects-' + this.sectionId);
      if (!this.variantSelects) return;

      this.variationsDiv = this.variantSelects.querySelector('.variations');
      if (!this.variationsDiv) return;

      const mapEl = document.getElementById('CombinedListingsMap-' + this.sectionId);
      if (!mapEl) { console.warn('[combined-listings] map not found'); return; }
      this.familyMap = JSON.parse(mapEl.textContent);
      if (!this.familyMap.length) { console.warn('[combined-listings] map is empty'); return; }

      this.cache = {};

      // Fully disable VariantSelects interference — we manage everything.
      // onVariantChange was bound to the prototype method at construction time,
      // so setting an instance property doesn't stop the listener. Remove it properly.
      const vsProto = Object.getPrototypeOf(this.variantSelects);
      this.variantSelects.removeEventListener('change', vsProto.onVariantChange);
      // Also block renderProductInfo in case anything else triggers it
      this.variantSelects.renderProductInfo = () => {};

      this.buildTypeFieldset();
    }

    buildTypeFieldset() {
      // Remove any existing type fieldset we built
      const existing = this.variationsDiv.querySelector('fieldset.cl-type-fieldset');
      if (existing) existing.remove();

      const fieldset = document.createElement('fieldset');
      fieldset.className = 'product-form__input product-form__input--block cl-type-fieldset';
      fieldset.dataset.index = 'option0';
      fieldset.dataset.handle = 'type';

      const labelDiv = document.createElement('div');
      labelDiv.className = 'form__label';
      const labelValue = document.createElement('span');
      labelValue.className = 'form__label__value';
      labelDiv.appendChild(document.createTextNode(this.typeLabel + ': '));
      labelDiv.appendChild(labelValue);
      fieldset.appendChild(labelDiv);

      this.familyMap.forEach((entry, i) => {
        const inputId = this.sectionId + '-type-' + i;
        const input = document.createElement('input');
        input.type = 'radio';
        input.id = inputId;
        input.name = 'cl-type-' + this.sectionId;
        input.value = entry.type || entry.title;
        if (i === 0) {
          input.checked = true;
          labelValue.textContent = input.value;
        }

        const lbl = document.createElement('label');
        lbl.setAttribute('for', inputId);
        lbl.innerHTML = '<span>' + (entry.type || entry.title) + '</span>';

        fieldset.appendChild(input);
        fieldset.appendChild(lbl);
      });

      // Insert before the existing size fieldset (option1)
      const sizeFieldset = this.variationsDiv.querySelector('fieldset[data-index="option1"]');
      if (sizeFieldset) {
        this.variationsDiv.insertBefore(fieldset, sizeFieldset);
      } else {
        this.variationsDiv.prepend(fieldset);
      }

      fieldset.addEventListener('change', (e) => {
        if (e.target.tagName !== 'INPUT') return;
        e.stopPropagation();
        const lv = fieldset.querySelector('.form__label__value');
        if (lv) lv.textContent = e.target.value;
        const entry = this.familyMap.find(p => (p.type || p.title) === e.target.value);
        if (entry) this.loadChild(entry);
      });

      // Load first product
      this.loadChild(this.familyMap[0]);
    }

    loadChild(entry) {
      // A combined listing's product_url can carry ?variant=..., and the .js
      // endpoint has to sit on the path, not after the query string:
      // /products/x?variant=1.js answers 200 with the HTML product page, so
      // r.json() throws and that member never loads.
      const key = entry.url.split('#')[0].split('?')[0];
      if (this.cache[key]) {
        this.applyChild(this.cache[key]);
        return;
      }

      fetch(key + '.js')
        .then(r => r.json())
        .then(productData => {
          this.cache[key] = productData;
          this.applyChild(productData);
        })
        .catch(e => console.error('[combined-listings] fetch error:', e));
    }

    applyChild(productData) {
      this.variationsDiv = this.variantSelects.querySelector('.variations');
      const variants = productData.variants;

      // The size option carries a different name on every store (Størrelse,
      // Storlek, Größe, Maat, Taille, Taglia ...). Matching only the Danish and
      // English names made sizeKey fall back to option1 — which on a child
      // product is Type, not size — so the picker listed "Dame" as a size.
      const SIZE_NAMES = [
        'size', 'sizes',
        'størrelse', 'storrelse', 'str',
        'storlek', 'størrelser',
        'größe', 'grösse', 'groesse', 'grosse',
        'maat', 'taille', 'taglia', 'koko', 'talla', 'tamanho'
      ];
      const sizeOptionIndex = productData.options
        ? productData.options.findIndex(o => SIZE_NAMES.includes(o.name.trim().toLowerCase()))
        : -1;
      const sizeKey = sizeOptionIndex >= 0 ? 'option' + (sizeOptionIndex + 1) : 'option1';

      const seen = {};
      const sizes = [];
      variants.forEach(v => {
        const size = v[sizeKey];
        if (size && !seen[size]) {
          seen[size] = true;
          sizes.push({ value: size, available: v.available, variantId: v.id });
        }
      });

      let sizeFieldset = this.variationsDiv.querySelector('fieldset[data-index="option1"]');
      if (!sizeFieldset) {
        sizeFieldset = document.createElement('fieldset');
        sizeFieldset.dataset.index = 'option1';
        this.variationsDiv.appendChild(sizeFieldset);
      }

      const prevChecked = sizeFieldset.querySelector('input:checked');
      const prevSize = prevChecked ? prevChecked.value : null;

      sizeFieldset.className = 'product-form__input product-form__input--block';
      sizeFieldset.dataset.handle = 'storrelse';
      sizeFieldset.innerHTML = '';

      const labelIndex = sizeOptionIndex >= 0 ? sizeOptionIndex : 0;
      const optionName =
        (productData.options && productData.options[labelIndex]?.name) || 'Size';

      const labelDiv = document.createElement('div');
      labelDiv.className = 'form__label';
      const labelValue = document.createElement('span');
      labelValue.className = 'form__label__value';
      labelDiv.appendChild(document.createTextNode(optionName + ': '));
      labelDiv.appendChild(labelValue);
      sizeFieldset.appendChild(labelDiv);

      const preferred = sizes.find(s => s.value === prevSize && s.available)
        || sizes.find(s => s.available)
        || sizes[0];

      if (preferred) labelValue.textContent = preferred.value;

      sizes.forEach((size, i) => {
        const inputId = this.sectionId + '-size-' + i;
        const input = document.createElement('input');
        input.type = 'radio';
        input.id = inputId;
        input.name = optionName;
        input.value = size.value;
        input.setAttribute('form', 'product-form-' + this.sectionId);
        if (!size.available) input.classList.add('is-disabled');
        if (preferred && size.value === preferred.value) input.checked = true;

        const lbl = document.createElement('label');
        lbl.setAttribute('for', inputId);
        lbl.innerHTML = '<span>' + size.value + '</span>';

        sizeFieldset.appendChild(input);
        sizeFieldset.appendChild(lbl);
      });

      this.updateImage(productData);
      this.updateForm(variants, sizeKey, preferred ? preferred.value : null);

      if (this._sizeChangeHandler) sizeFieldset.removeEventListener('change', this._sizeChangeHandler);
      this._sizeFieldset = sizeFieldset;
      this._sizeChangeHandler = (e) => {
        if (e.target.tagName !== 'INPUT') return;
        e.stopPropagation();
        const lv = sizeFieldset.querySelector('.form__label__value');
        if (lv) lv.textContent = e.target.value;
        this.updateForm(variants, sizeKey, e.target.value);
      };
      sizeFieldset.addEventListener('change', this._sizeChangeHandler);
    }

    updateImage(productData) {
      const imgUrl = productData.featured_image;
      if (!imgUrl) return;

      const productWrapper = this.variantSelects.closest('.thb-product-detail');
      if (!productWrapper) return;

      const slides = productWrapper.querySelectorAll('.product-images__slide');
      if (!slides.length) return;

      // Replace all slides with child product images (or just first if fewer images)
      const childImages = productData.images && productData.images.length
        ? productData.images
        : [imgUrl];

      slides.forEach((slide, i) => {
        const img = slide.querySelector('img');
        if (!img) return;
        const rawUrl = (childImages[i] || childImages[0]).split('?')[0];
        const srcset = [180, 360, 540, 720, 900, 1080].map(w => `${rawUrl}?width=${w} ${w}w`).join(', ');
        img.src = `${rawUrl}?width=720`;
        img.setAttribute('data-src', `${rawUrl}?width=720`);
        img.setAttribute('data-srcset', srcset);
        img.srcset = srcset;
        img.classList.remove('lazyloaded');
        img.classList.add('lazyload');
      });

      // Go to first slide
      const productSlider = productWrapper.querySelector('.product-images');
      if (productSlider && window.Flickity) {
        const flkty = Flickity.data(productSlider);
        if (flkty) flkty.select(0);
      }
    }

    updateForm(variants, sizeKey, sizeValue) {
      const v = variants.find(variant => variant[sizeKey] === sizeValue);
      if (!v) return;

      document.dispatchEvent(new CustomEvent('product:variant-change', {
        bubbles: true,
        detail: { variant: v, sectionId: this.sectionId }
      }));

      document.querySelectorAll('#product-form-' + this.sectionId + ', #product-form-installment').forEach(form => {
        const input = form.querySelector('input[name="id"]');
        if (input) { input.value = v.id; input.removeAttribute('disabled'); }
      });

      const addBtn = document.querySelector('.single-add-to-cart-button');
      if (!addBtn) return;
      const btnText = addBtn.querySelector('.single-add-to-cart-button--text');
      if (v.available) {
        addBtn.removeAttribute('disabled');
        addBtn.classList.remove('sold-out');
        if (btnText && window.theme && window.theme.variantStrings) btnText.textContent = window.theme.variantStrings.addToCart;
      } else {
        addBtn.setAttribute('disabled', 'disabled');
        addBtn.classList.add('sold-out');
        if (btnText && window.theme && window.theme.variantStrings) btnText.textContent = window.theme.variantStrings.soldOut;
      }

      // Re-render inventory, price and sku from Shopify section
      const vs = this.variantSelects;
      fetch(`${vs.dataset.url}?variant=${v.id}&section_id=${vs.dataset.section}`)
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          ['inventory', 'price', 'sku'].forEach(prefix => {
            const id = `${prefix}-${vs.dataset.section}`;
            const src = doc.getElementById(id);
            const dst = document.getElementById(id);
            if (src && dst) dst.innerHTML = src.innerHTML;
          });
        });
    }
  });
}
